import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { PICKING_METHODS, Steps } from './constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { useLocalStorage, PICKING_METHOD_PREFERENCE } from '../../../../hooks/use-local-storage';
import { InventoryService } from '../../../../services/inventory-service';
import Global from '../../../../store/global-selectors';

/** @typedef {import('../../../../prop-types/ship-from').ShipFromPropType} ShipFromPropType */

/**
 * @typedef {object} ShipFromState
 * @property {string} shipFromId - The currently selected ship from ID.
 * @property {Array<ShipFromPropType>} sellerShipFroms - List of ship from locations available for the seller.
 * @property {(string) => void} setShipFromId - Function to update the selected ship from ID.
 */

/**
 * @returns {ShipFromState}
 */
export const useShipFromState = () => {
  const defaultShipFromId = useSelector(Global.selectSellerDefaultShipFromId);
  const sellerShipFroms = useSelector((state) => {
    const rawSellerShipFroms = Global.selectSellerShipFroms(state) || emptyArray;
    return rawSellerShipFroms.map((shipFrom) => ({
      id: shipFrom.shipFromId,
      ...shipFrom,
    }));
  });
  const [shipFromId, setShipFromId] = useState(defaultShipFromId ?? sellerShipFroms?.[0]?.shipFromId ?? '');

  return useMemo(
    () => ({
      shipFromId,
      sellerShipFroms,
      setShipFromId,
    }),
    [shipFromId, sellerShipFroms],
  );
};

/**
 * @typedef {object} SetupOrdersState
 * @property {string} batchName
 * @property {boolean} canSetupOrdersContinue
 * @property {boolean} doesShipFromHaveInventory
 * @property {boolean} doesShipFromHaveNativeInventory
 * @property {string} pickingMethod
 * @property {(string) => void} setBatchName
 * @property {(string) => void} setPickingMethod
 * @property {() => Promise<void>} onSetupOrdersContinue
 */

/**
 * @param {object} params
 * @param {boolean} params.isBatch
 * @param {string} params.initialBatchName
 * @param {boolean} params.inventoryTrackingIsEnabled
 * @param {() => Promise<void>} params.prepareVerifyOrdersStep
 * @param {(number) => void} params.setCurrentStep
 * @param {number} params.shipFromId
 * @returns {SetupOrdersState}
 */
export const useSetupOrdersState = ({
  isBatch,
  initialBatchName,
  inventoryTrackingIsEnabled,
  prepareVerifyOrdersStep,
  setCurrentStep,
  shipFromId,
}) => {
  const [batchName, setBatchName] = useState(initialBatchName);
  const [pickingMethod, setPickingMethod] = useLocalStorage(PICKING_METHOD_PREFERENCE, PICKING_METHODS.BATCH);
  const doesShipFromHaveInventory = useSelector(
    (state) => Global.selectSellerShipFromsById(state, { shipFromId })?.isAssociatedToInventorySource || false,
  );
  const doesShipFromHaveNativeInventory = useSelector((state) =>
    InventoryService.doesShipFromHaveNativeInventoryTrackingEnabled(shipFromId, state),
  );

  return useMemo(() => {
    const completeSetupOrdersStep = () => {
      // No work to do other than moving to next step, these requests will be performed later
      setCurrentStep(Steps.checkForOrderErrors);
    };
    const onSetupOrdersContinue = async () => {
      completeSetupOrdersStep();
      await prepareVerifyOrdersStep();
    };
    const isBatchNameValid = !isBatch || pickingMethod === PICKING_METHODS.QUEUE || batchName?.length > 0;
    const shouldRequireInventory = inventoryTrackingIsEnabled || pickingMethod === PICKING_METHODS.QUEUE;
    const isShipFromValid = shipFromId > 0 && (!shouldRequireInventory || (doesShipFromHaveInventory && doesShipFromHaveNativeInventory));

    const canSetupOrdersContinue = isBatchNameValid && isShipFromValid;
    return {
      batchName,
      canSetupOrdersContinue,
      doesShipFromHaveInventory,
      doesShipFromHaveNativeInventory,
      pickingMethod,
      onSetupOrdersContinue,
      setBatchName,
      setPickingMethod,
    };
  }, [
    batchName,
    doesShipFromHaveInventory,
    doesShipFromHaveNativeInventory,
    inventoryTrackingIsEnabled,
    isBatch,
    pickingMethod,
    prepareVerifyOrdersStep,
    setCurrentStep,
    setPickingMethod,
    shipFromId,
  ]);
};
